import React from 'react';
import { Menu, MenuItems } from '@headlessui/react';
import { formatDateToLocal, getDateTimePatterns } from 'utils/date';
import { StackedListMenuItem, StackedListMenuToggle } from 'components/stackedLists';
import { Badge } from 'components/badges';
import {
  WORKER_TYPES,
  WORKER_TYPE_ICONS,
  WORKER_TYPES_LABELS
} from 'constants/workers';

export default function WorkerStackedListItem({ item, settings, onAction }) {
  const { dateTimePattern } = getDateTimePatterns(settings);

  const createdDate = formatDateToLocal(item.createdAt, dateTimePattern);

  const handleAction = (action) => {
    if (onAction) {
      onAction(action, item);
    }
  };

  const isParser = item.type === WORKER_TYPES.PARSER;
  const WorkerParserIcon = WORKER_TYPE_ICONS[WORKER_TYPES.PARSER];
  const WorkerPrebuildIcon = WORKER_TYPE_ICONS[WORKER_TYPES.PREBUILD];

  return (
    <li className="flex justify-between gap-x-6 py-5 px-4 hover:bg-gray-800/50 transition-colors">
      <div className="flex min-w-0 gap-x-4">
        <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-800 ring-1 ring-gray-700">
          {isParser ? (
            <WorkerParserIcon className="h-6 w-6 text-indigo-400" aria-hidden="true" />
          ) : (
            <WorkerPrebuildIcon className="h-6 w-6 text-indigo-400" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-auto">
          <div className="flex items-center gap-x-3">
            <p className="text-sm font-semibold leading-6 text-white">{item.name}</p>
            <Badge color="indigo">
              {isParser ? WORKER_TYPES_LABELS[WORKER_TYPES.PARSER] : WORKER_TYPES_LABELS[WORKER_TYPES.PREBUILD]}
            </Badge>
          </div>
          <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-x-2 text-xs leading-5 text-gray-400">
            {isParser && item.config?.url && (
              <>
                <span className="font-medium text-gray-300">{item.config?.method}</span>
                <p className="truncate max-w-xs">{item.config.url}</p>
                <svg viewBox="0 0 2 2" className="hidden sm:inline h-0.5 w-0.5 fill-current">
                  <circle cx={1} cy={1} r={1} />
                </svg>
              </>
            )}
            <p className="whitespace-nowrap">
              Created on <time dateTime={item.createdAt}>{createdDate}</time>
            </p>
          </div>
          {item.activeReminderCount > 0 && (
            <div className="mt-1 text-xs leading-5 text-gray-400">
              Attached to {item.activeReminderCount} {item.activeReminderCount === 1 ? 'reminder' : 'reminders'}
            </div>
          )}
        </div>
      </div>
      <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end justify-center">
        <div className="flex items-center gap-x-4">
          <button
            type="button"
            onClick={() => handleAction('view')}
            className="rounded-md bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-white/20 cursor-pointer"
          >
            View Worker
          </button>
          <Menu as="div" className="relative flex-none">
            <StackedListMenuToggle />
            <MenuItems
              transition
              anchor="bottom end"
              className="w-48 origin-top-right rounded-md bg-gray-800 py-2 shadow-lg ring-1 ring-white/10 focus:outline-none transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <StackedListMenuItem onClick={() => handleAction('view')}>
                Edit
              </StackedListMenuItem>
              <StackedListMenuItem onClick={() => handleAction('add-worker')}>
                Add to Reminder
              </StackedListMenuItem>
              <StackedListMenuItem variant="danger" onClick={() => handleAction('delete')}>
                Delete
              </StackedListMenuItem>
            </MenuItems>
          </Menu>
        </div>
      </div>
    </li>
  );
}
